import { deepCopy } from '../../util/deep-copy';
import type { DefinitionsService } from '../docker-compose';
import {
  type Dict,
  dockerComposeToString,
  dumpEnv,
  dumpYaml,
  findSetting,
  formatHostRules,
  getAppDirName,
  parseDockerCompose,
  replaceVariables,
} from '../util';
import {
  type AdditionalDNSOptions,
  CLOVYR_ACCESS_KEY,
  CLOVYR_BACKUP_PASSWORD,
  CLOVYR_CONFIGURE_KEY,
  CLOVYR_CUSTOM_DOMAIN,
  CLOVYR_DOCKER_PROXY,
  CLOVYR_S3_URL,
  CLOVYR_SECRET_KEY,
  CLOVYR_TREFOIL_URL,
  CLOVYR_WNEXT_FQDN,
  DeploymentAddons,
  DeploymentMethod,
  type DockerCompose,
  type Files,
  type Manifest,
  type Values,
} from '..';

import type { ConfigureBuilder } from '.';
import { firewall } from './common';
import type { PostProcessFilesFn } from './configure-builder';
import traefikAuth from './traefik-auth.yaml?raw';
import traefikCompose from './traefik-compose.yaml?raw';
import traefikConfig from './traefik-config.yaml?raw';
import { isDNSProxied, writeAllFilesBase64, writeBase64File } from './util';

const TRAEFIK_CERTS_PATH = '/opt/clovyr/traefik/certs';

type DependsOnCondition = 'service_started' | 'service_healthy' | 'service_completed_successfully';

interface TraefikRule {
  serviceName: string;
  isCustom: boolean;
  isPrimary: boolean;
  labels: Dict;
}

/**
 * Add a traefik router+service to the Docker Compose service for each exposed
 * service in the manifest
 *
 * @param manifest
 * @param dc
 */
function addTraefikLabels(manifest: Manifest, dc: DockerCompose, values: Values) {
  const proxyDNS = isDNSProxied(manifest, values[CLOVYR_CUSTOM_DOMAIN] as string);
  const dcConfig = manifest.deployment?.['docker-compose'];
  const services = dcConfig?.services;
  if (!services || !dc.services) {
    return;
  }

  const fqdn = values[CLOVYR_WNEXT_FQDN] as string;
  const customDomain = values[CLOVYR_CUSTOM_DOMAIN] as string;

  // default rules, i.e., not the additional host rules
  // need to be added at the very end so we can detect the primary host
  let defaultRules: TraefikRule[] = [];
  Object.entries(services).forEach(([serviceName, portOpts]) => {
    const port = typeof portOpts === 'number' ? portOpts : portOpts.port;
    const svc = dc.services![serviceName];
    if (!svc) {
      throw new Error(`Service '${serviceName}' not found in Docker Compose file`);
    }
    svc.labels ||= {};

    // Use custom routing rule if available
    let isCustom = true;
    let rule = dcConfig.proxy?.rules?.[serviceName];
    if (!rule) {
      // use standard host routing rule
      // routes all subdomains and "*-<host>" subs as well, as a fallback
      isCustom = false;
      rule = `HostRegexp(\`^(.*?[\\.-])?${fqdn}$\`)`;
      if (customDomain) {
        rule += ` || HostRegexp(\`^(.*?[\\.-])?${customDomain}$\`)`;
      }
    }

    // traefik auth middleware is disabled for now, due to complications
    // with setting our auth cookie across different browsers. Firefox,
    // for example, blocks third-party cookies on an XHR as part of it's
    // tracking protection feature.
    const middlewares = ''; // leaf_auth@file

    const isPrimary = !!(typeof portOpts !== 'number' && portOpts.primary);
    defaultRules.push({
      serviceName,
      isCustom,
      isPrimary,
      labels: createTraefikConfig(serviceName, rule, middlewares, port, proxyDNS),
    });

    // add separate labels for each additional host
    if (manifest.deployment?.dns?.additional_hosts) {
      Object.entries(manifest.deployment.dns.additional_hosts).forEach(([host, service]) => {
        const opts: AdditionalDNSOptions = {} as AdditionalDNSOptions;

        if (typeof service === 'string') {
          const matches = service.match(new RegExp(`^${serviceName}(:\\d+)?$`));
          if (!matches) {
            return;
          }
          const additionalPortStr = matches?.[1];
          opts.port = additionalPortStr ? parseInt(additionalPortStr.slice(1), 10) : port;
        } else {
          if (serviceName !== service.service) {
            return;
          }
          Object.assign(opts, service);
          opts.port ||= port;
        }

        if (opts.proxy === false) {
          // skip adding labels for this service
          return;
        }

        // create specific host rule for the extra hostname
        const hosts = formatHostRules(
          values[CLOVYR_WNEXT_FQDN] as string,
          values[CLOVYR_CUSTOM_DOMAIN] as string,
          host
        );
        const additionalHostRule = `Host(${hosts})`;
        // add traefik labels for the extra hostname
        Object.assign(
          svc.labels!,
          createTraefikConfig(`${host}-extra`, additionalHostRule, middlewares, opts.port, proxyDNS)
        );
      });
    }
  });

  if (!defaultRules.find((r) => r.isPrimary)) {
    // mark the first non-custom rule as primary
    for (let i = 0; i < defaultRules.length; i++) {
      if (!defaultRules[i].isCustom) {
        defaultRules[i].isPrimary = true;
        break;
      }
    }
  }
  const primaryRule = defaultRules.find((r) => r.isPrimary);
  if (primaryRule) {
    let main = fqdn;
    let sans = '';
    if (customDomain) {
      main = customDomain;
      sans = fqdn;
    }
    Object.assign(primaryRule.labels, {
      [`traefik.http.routers.${primaryRule.serviceName}.tls.domains[0].main`]: main,
      [`traefik.http.routers.${primaryRule.serviceName}.tls.domains[0].sans`]: sans,
    });
  }

  // clear non-custom rules so only the primary service routed at the root domain
  defaultRules = defaultRules.filter((r) => r.isCustom || r.isPrimary);

  // add default rule
  defaultRules.forEach((rule) => {
    Object.assign(dc.services![rule.serviceName].labels!, rule.labels);
  });
}

/**
 * Create standard traefik labels for the given service
 *
 * @param serviceName
 * @param routingRule
 * @param middlewares
 * @param port
 * @returns
 */
function createTraefikConfig(
  serviceName: string,
  routingRule: string,
  middlewares: string,
  port: number,
  proxyDNS: boolean
): Dict {
  const labels = {
    'traefik.enable': 'true',
    [`traefik.http.routers.${serviceName}.tls`]: 'true',
    [`traefik.http.routers.${serviceName}.rule`]: routingRule,
    [`traefik.http.routers.${serviceName}.service`]: serviceName,
    [`traefik.http.services.${serviceName}.loadbalancer.server.port`]: `${port}`,
  };

  if (middlewares?.length) {
    labels[`traefik.http.routers.${serviceName}.middlewares`] = middlewares;
  }

  if (!proxyDNS) {
    // when not proxying (via cdn/cloudflare), ensure that lets encrypt is enabled in traefik
    labels[`traefik.http.routers.${serviceName}.tls.certresolver`] = 'default';
  }

  return labels;
}

/**
 * Attach addon services as requested by manifest
 *
 * @param manifest
 * @param dc
 */
function addDeploymentAddons(manifest: Manifest, dc: DockerCompose, values: Values) {
  const proxyDNS = isDNSProxied(manifest, values[CLOVYR_CUSTOM_DOMAIN] as string);
  if (manifest.deployment && manifest.deployment.addons) {
    if (manifest.deployment.addons[DeploymentAddons.MailHog]) {
      addMailhog(dc, proxyDNS);
    }
  }
}

/**
 * Add mailhog service to the compose file
 *
 * @param dc
 * @param proxyDNS
 */
function addMailhog(dc: DockerCompose, proxyDNS: boolean) {
  // synapse: Host(`{{ HOSTNAME }}`)
  const rule = 'Host(`{{ HOSTNAME }}`) && PathPrefix(`/_leaf/mail/`)';
  const middlewares = 'leaf_cors@file,leaf_auth@file';
  const labels = createTraefikConfig('clovyr-mailhog', rule, middlewares, 8025, proxyDNS);
  labels[`traefik.http.routers.clovyr-mailhog.priority`] = '1001';
  const origin = typeof window !== 'undefined' ? window.location.hostname : 'clovyr.app';
  dc.services!['clovyr-mailhog'] = {
    image: 'clovyr/mailhog:latest',
    restart: 'unless-stopped',
    logging: {
      driver: 'none', // disable saving logs
    },
    environment: {
      MH_CORS_ORIGIN: `https://${origin}`,
      MH_UI_WEB_PATH: '_leaf/mail',
    },
    labels,
  };
}

/**
 * Process the App Config Values. Adds vars to either the service's environment
 * var map or to a shared clovyr.env file.
 *
 * @param manifest
 * @param values
 * @param dc
 * @returns
 */
export function addConfigEnv(manifest: Manifest, values: Values, dc: DockerCompose): Dict {
  const clovyrEnv: Dict = {};
  if (!values || Object.keys(values).length === 0) {
    return clovyrEnv;
  }

  // add to each service, if key is present in declared env
  const { services } = dc;
  if (!services) {
    return clovyrEnv;
  }

  Object.keys(values).forEach((valueName) => {
    let found = false;
    const cfg = findSetting(valueName, manifest);
    Object.keys(services).forEach((svcName) => {
      const sv = services[svcName];
      const env = sv.environment;
      if (typeof env === 'object') {
        const envKeys = Object.keys(env);
        if (envKeys.includes(valueName)) {
          env[valueName] = values[valueName];
          // always replace directly in env vars
          // but only skip adding to clovyr.env if
          if (cfg && cfg.secure) {
            found = true;
          }
        }
      }
    });
    if (!found || (cfg && !cfg.secure)) {
      // add remaining keys to clovyrEnv
      // TODO: add all keys here no matter what?
      // this is currently to avoid passing sensitive vars to all services,
      // e.g., don't pass postgres root pw to all services.
      clovyrEnv[valueName] = values[valueName];
    }
  });

  Object.keys(services).forEach((svcName) => {
    const sv = services[svcName];
    if (Array.isArray(sv.env_file)) {
      sv.env_file.push('.env');
    }
  });

  return clovyrEnv;
}

/**
 * Create and assign a unique bridge network for this compose app
 *
 * @param networkName
 * @param dc
 */
function addNetworks(manifest: Manifest, dc: DockerCompose) {
  const dcConfig = manifest.deployment?.['docker-compose'];
  if (dcConfig) {
    const { networks } = dcConfig;
    if (networks && networks.length > 0) {
      // add defined networks to our addon containers (so far just mailhog??)
      const mh = dc.services?.['clovyr-mailhog'];
      if (mh) {
        mh.networks ||= [];
        networks.forEach((name) => {
          if (Array.isArray(mh.networks)) {
            mh.networks.push(name);
          }
        });
      }
      return; // don't do anything when explicitly set
    }

    // add network using app id name if it doesn't  exist
    const networkName = manifest.metadata.id;
    addNetwork(networkName, dc);
    dcConfig.networks = [networkName];
  }
}

/**
 * Create a new network and add it to each service
 *
 * @param networkName
 * @param dc
 * @returns
 */
function addNetwork(networkName: string, dc: DockerCompose) {
  dc.networks ||= {};
  if (dc.networks[networkName]) {
    return;
  }
  dc.networks[networkName] = {
    name: networkName,
    driver: 'bridge',
  };

  // assign network to each service
  if (!dc.services) {
    return;
  }
  Object.values(dc.services).forEach((s) => {
    s.networks ||= {};
    if (!s.network_mode || s.network_mode.length === 0) {
      s.networks[networkName] ||= {};
    }
  });
}

/**
 * Add bind volume to the given service
 *
 * @param svc
 * @param source
 * @param target
 */
function addVolume(svc: DefinitionsService, source: string, target: string) {
  if (!svc.volumes) {
    svc.volumes = [];
  }
  const vols = svc.volumes;
  if (vols.length === 0 || typeof vols[0] === 'string') {
    // add volume as string type
    vols.push(`${source}:${target}`);
  } else {
    // add volume as object type
    vols.push({ type: 'bind', source, target });
  }
}

/**
 * Mount the TLS certs volume to each requesting service
 *
 * @param manifest
 * @param dc
 * @returns
 */
function addTLSVolume(manifest: Manifest, dc: DockerCompose) {
  if (!(manifest.deployment?.dns?.proxy === false && manifest.deployment?.dns?.tls?.mount)) {
    return;
  }

  Object.entries(manifest.deployment.dns.tls.mount).forEach(([serviceName, path]) => {
    // add as mount on service
    if (!dc.services![serviceName].volumes) {
      dc.services![serviceName].volumes = [];
    }
    addVolume(dc.services![serviceName], TRAEFIK_CERTS_PATH, path);
  });
}

/**
 * Add traefik-related network and label config to compose file
 *
 * @param manifest Application manifest
 * @param composeFile The contents of the docker-compose.yml file for the given application
 * @returns Modified DockerCompose object
 */
export function mkCompose(manifest: Manifest, values: Values, composeFile: string): DockerCompose {
  const dc = parseDockerCompose(composeFile);

  addDeploymentAddons(manifest, dc, values);
  addNetworks(manifest, dc);
  addTraefikLabels(manifest, dc, values);
  addTLSVolume(manifest, dc);
  attachRestic(manifest, values, dc);

  return dc;
}

/**
 * Attach Restic sidecar if required by application manifest
 *
 * @param dc
 * @param appId
 */
function attachRestic(manifest: Manifest, values: Values, dc: DockerCompose) {
  if (!manifest.backup?.locations?.length) {
    return;
  }

  // initialize restic svc and add to compose file
  const backupSvc = newResticService(manifest, values);

  // attach volumes to svcs and collect paths
  const resticPaths: string[] = [];
  const seenVolumes = {};
  manifest.backup.locations.forEach((backupLocation) => {
    resticPaths.push(`/restic/${backupLocation.volume}${backupLocation.path}`);
    if (seenVolumes[backupLocation.volume]) {
      return;
    }
    seenVolumes[backupLocation.volume] = 1;
    backupSvc.volumes!.push(`${backupLocation.volume}:/restic/${backupLocation.volume}`);
  });
  (backupSvc.environment as Dict)['RESTIC_PATHS'] = resticPaths.join(' ');
  backupSvc.healthcheck = {
    test: ['CMD', 'curl', '-f', 'http://127.0.0.1:5134/healthz'],
    start_period: '30s',
    interval: '5s',
  };

  // create restore service
  // basic config is identical to backup with a couple changes
  const restoreWaitSvc = newResticService(manifest, values);
  restoreWaitSvc.command = ['restore-wait'];
  restoreWaitSvc.restart = 'no';
  restoreWaitSvc.depends_on = {
    'clovyr-restic-backup': {
      condition: 'service_healthy' as DependsOnCondition,
    },
  };

  // add restore container as dep to all services
  const restoreDep = {
    'clovyr-restic-restore': {
      condition: 'service_completed_successfully' as DependsOnCondition,
    },
  };
  Object.values(dc.services!).forEach((svc) => {
    svc.depends_on = { ...svc.depends_on, ...deepCopy(restoreDep) };
  });

  // add restic services
  dc.services!['clovyr-restic-backup'] = backupSvc;
  dc.services!['clovyr-restic-restore'] = restoreWaitSvc;
}

/**
 * Create service stub for restic service (restore or backup)
 *
 * @param manifest
 * @param values
 * @returns
 */
function newResticService(manifest: Manifest, values: Values): DefinitionsService {
  return {
    image: 'clovyr/restic:latest',
    restart: 'always',
    init: true,
    network_mode: 'host',
    environment: {
      // TODO: instead of using manifest id, use fqdn name for repo?
      RESTIC_REPOSITORY: `${values[CLOVYR_S3_URL]}/${manifest.metadata.id}`,
      AWS_ACCESS_KEY_ID: values[CLOVYR_ACCESS_KEY],
      AWS_SECRET_ACCESS_KEY: values[CLOVYR_SECRET_KEY],
      RESTIC_PASSWORD: values[CLOVYR_BACKUP_PASSWORD],
      FQDN: values[CLOVYR_WNEXT_FQDN],
      CONFIGURE_KEY: values[CLOVYR_CONFIGURE_KEY],
      TREFOIL_URL: values[CLOVYR_TREFOIL_URL],
    },
    volumes: [],
  };
}

/**
 * Attach service network to traefik compose file
 *
 * @param manifest
 * @returns {DockerCompose} traefik compose file
 */
export function mkTraefikCompose(manifest: Manifest): DockerCompose {
  const tc = parseDockerCompose(traefikCompose);
  tc.networks ||= {};

  // Give traefik access to each network used by the app as defined in the manifest
  const networks = manifest.deployment?.['docker-compose']?.networks;
  if (networks) {
    networks.forEach((network) => {
      if (tc.networks) {
        tc.networks[network] = {
          name: network,
          external: true,
        };
      }
      if (Array.isArray(tc.services?.['traefik']?.networks)) {
        tc.services['traefik'].networks.push(network);
      }
    });
  }
  return tc;
}

/**
 * Prepare data needed to build final configure script
 *
 * This exported method exists for easier testability (and composability) of
 * this codebase.
 *
 * @param manifest
 * @param values
 * @returns
 */
export function prepareConfigureData(
  manifest: Manifest,
  values: Values
): [DockerCompose, Dict, Files, string] {
  if (!manifest.deployment?.contents?.[DeploymentMethod.DockerCompose]) {
    throw new Error('manifest error: missing deployment contents');
  }

  const manifestCopy = deepCopy(manifest);

  // create a copy of files so we can replace docker-compose.yml with a modified version
  const files: Files = {
    ...manifest.deployment.contents[DeploymentMethod.DockerCompose],
  };

  let dc = mkCompose(manifestCopy, values, files['docker-compose.yml'].content);
  const clovyrEnv = addConfigEnv(manifestCopy, values, dc);
  files['.env'] = { content: dumpEnv(clovyrEnv) };
  files['docker-compose.yml'] = { content: dockerComposeToString(dc) };

  // replace supported variables in all files
  Object.keys(files).forEach((file) => {
    if (files[file].encoding !== 'base64') {
      files[file].content = replaceVariables(files[file].content, values);
    }
  });

  files['clovyr-values.yml'] = { content: dumpYaml(values) };

  const traefikComposeFile = dockerComposeToString(mkTraefikCompose(manifestCopy));

  // return modified compose file
  dc = parseDockerCompose(files['docker-compose.yml'].content);

  return [dc, clovyrEnv, files, traefikComposeFile];
}

export interface ConfigureOpts {
  /**
   * Base bootstrap sets up the system for running applications via Docker Compose. This can be
   * overridden to use some other runtime, e.g., containerd+nerdctl.
   */
  baseBootstrap: string;
  pullCmd: string;
  upCmd: string;

  /**
   * List of systemd services that need to be proxied
   */
  proxyServices: string[];
}

/**
 *  Add proxy setup to the base bootstrap script
 *
 * @param proxy URL
 * @param opts
 * @returns
 */
function addProxySetup(proxy: string, opts: ConfigureOpts) {
  if (!proxy) {
    return;
  }
  opts.proxyServices.forEach((service) => {
    opts.baseBootstrap += `
sed -i '/^\\[Service\\]/a Environment="HTTP_PROXY=${proxy}"\\nEnvironment="HTTPS_PROXY=${proxy}"' "${service}"
`;
  });
  opts.baseBootstrap += `
systemctl daemon-reload
systemctl restart ${opts.proxyServices.map((s) => `"$(basename "${s}")"`).join(' ')}
    `;
  // prepend commands with proxy vars
  opts.upCmd = `HTTP_PROXY=${proxy} HTTPS_PROXY=${proxy} ${opts.upCmd}`;
  opts.pullCmd = `HTTP_PROXY=${proxy} HTTPS_PROXY=${proxy} ${opts.pullCmd}`;
  opts.baseBootstrap = `
curl -sL ${proxy}/ca.crt > /usr/share/ca-certificates/docker_registry_proxy.crt
echo "docker_registry_proxy.crt" >> /etc/ca-certificates.conf
update-ca-certificates --fresh

${opts.baseBootstrap}
`;
}

export function mkConfigureScriptWithOpts(
  manifest: Manifest,
  values: Values,
  opts: ConfigureOpts,
  fn?: PostProcessFilesFn
): string {
  const [, , files, traefikComposeFile] = prepareConfigureData(manifest, values);

  if (fn) {
    fn(files);
  }

  // add proxy configs
  const proxy = values[CLOVYR_DOCKER_PROXY] as string;
  if (proxy) {
    addProxySetup(proxy, opts);
  }

  // dynamic traefik CORS allowed origins. add the local public url
  const traefikOriginsArr = ['https://clovyr.app'];
  if (values[CLOVYR_TREFOIL_URL]) {
    traefikOriginsArr.push((values[CLOVYR_TREFOIL_URL] as string).replace('/api', ''));
  }
  const traefikOrigins = traefikOriginsArr.map((o) => `'${o}'`).join(', ');

  const appDir = getAppDirName(manifest);

  // custom bootstrap step -----------
  let customBootstrap = '';
  if (manifest.deployment?.bootstrap?.custom_script) {
    customBootstrap = `
    (cat <<-'EOF'

${manifest.deployment.bootstrap.custom_script}

EOF
    ) > /tmp/clovyr_custom_bootstrap.sh
    bash /tmp/clovyr_custom_bootstrap.sh
    rm -f /tmp/clovyr_custom_bootstrap.sh
    `;
  }
  // end custom bootstrap ------------

  const script = `#!/bin/bash
set -euxo pipefail

${mkDebugSSHScript()}

${firewall}

${opts.baseBootstrap}

${customBootstrap}

# disable web ssh access, if needed (ec2, lightsail)
dpkg -l ec2-instance-connect 2>&1 >/dev/null \
  && ( apt-get remove -qqy ec2-instance-connect \
    && sed -e '/^TrustedUserCAKeys/s/^/#/' -i /etc/ssh/sshd_config \
    && systemctl restart sshd.service ) &

# install traefik
mkdir -p /opt/clovyr/traefik/conf/dynamic
${writeBase64File('/opt/clovyr/traefik/docker-compose.yml', traefikComposeFile)}
${writeBase64File('/opt/clovyr/traefik/conf/traefik.yml', traefikConfig)}
${writeBase64File(
  '/opt/clovyr/traefik/conf/dynamic/forward-auth.yml',
  replaceVariables(traefikAuth, { ...values, TRAEFIK_ORIGINS: traefikOrigins })
)}

pushd /opt/clovyr/traefik
${opts.pullCmd} &
popd

# install app
mkdir -p /opt/clovyr/apps/${appDir}
pushd /opt/clovyr/apps/${appDir}
${writeAllFilesBase64(files)}
${opts.upCmd}
popd

# start traefik
# wait for instance-configure to exit before we start traefik
# to avoid issues listening on 80/443
bash -c 'sleep 1; cd /opt/clovyr/traefik; ${opts.upCmd}' &
`;

  return script;
}

// BEGIN temporary SSH debugging.
function mkDebugSSHScript(): string {
  // localStorage.setItem('clovyr.debugSSH', JSON.stringify({ publicKey: 'ssh-ed25519 ...', sourceIP: '203.0.113.1/32' }));
  // sourceIP is optional (IPv4 address or CIDR). Only public keys belong here.
  let debugSSH = '';
  if (import.meta.env.SSR) {
    return debugSSH;
  }
  let debugStorage: Storage | undefined;
  try {
    debugStorage = typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    // Storage may be unavailable in restricted browser contexts.
  }
  const debugConfig = debugStorage?.getItem('clovyr.debugSSH');
  if (!debugConfig) {
    return debugSSH;
  }
  const debug: unknown = JSON.parse(debugConfig);
  if (
    !debug ||
    typeof debug !== 'object' ||
    !('publicKey' in debug) ||
    typeof debug.publicKey !== 'string'
  ) {
    console.warn('clovyr.debugSSH requires a publicKey');
    return debugSSH;
  }
  // Disallow authorized_keys options and additional lines; discard the key comment.
  const key = debug.publicKey
    .trim()
    .match(
      /^(ssh-ed25519|ssh-rsa|ecdsa-sha2-nistp(?:256|384|521)) ([A-Za-z0-9+/]+={0,2})(?: [^\r\n]*)?$/
    );
  if (!key) {
    console.warn('clovyr.debugSSH requires a single OpenSSH public key');
    return debugSSH;
  }
  let sourceOption = '';
  if ('sourceIP' in debug && debug.sourceIP !== undefined && debug.sourceIP !== '') {
    const source =
      typeof debug.sourceIP === 'string'
        ? debug.sourceIP.match(/^(\d{1,3}(?:\.\d{1,3}){3})(?:\/(\d{1,2}))?$/)
        : null;
    if (
      !source ||
      source[1].split('.').some((part) => Number(part) > 255) ||
      (source[2] !== undefined && Number(source[2]) > 32)
    ) {
      console.warn('clovyr.debugSSH sourceIP must be an IPv4 address or CIDR');
      return debugSSH;
    }
    sourceOption = `from="${debug.sourceIP}",`;
  }
  const authorizedKey = `${sourceOption}no-agent-forwarding,no-port-forwarding,no-X11-forwarding ${key[1]} ${key[2]}`;
  debugSSH = `
# BEGIN temporary SSH debugging (cloud firewall must allow TCP 22).
debug_user=''
for candidate in ubuntu ec2-user root; do
  if id "$candidate" >/dev/null 2>&1; then
    debug_user=$candidate
    break
  fi
done
if [ -z "$debug_user" ]; then
  echo 'Debug SSH setup failed: no supported login account found' >&2
  exit 1
fi
debug_home=$(getent passwd "$debug_user" | cut -d: -f6)
test -n "$debug_home"
install -d -m 700 -o "$debug_user" "$debug_home/.ssh"
touch "$debug_home/.ssh/authorized_keys"
chmod 600 "$debug_home/.ssh/authorized_keys"
chown "$debug_user" "$debug_home/.ssh/authorized_keys"
# Leading newline preserves any existing key lacking a trailing newline.
printf '\\n%s\\n' '${authorizedKey}' >> "$debug_home/.ssh/authorized_keys"
echo "Debug SSH configured for user: $debug_user"
unset debug_user debug_home candidate
# END temporary SSH debugging.
`;
  console.warn(
    "SSH debugging enabled. To disable for future launches, run localStorage.removeItem('clovyr.debugSSH'). Existing instances retain the installed SSH key."
  );
  return debugSSH;
}
// END temporary SSH debugging.

function mkConfigureScript(manifest: Manifest, values: Values, fn?: PostProcessFilesFn): string {
  const baseBootstrap = `
# --- base bootstrap

# stargz snapshotter
export NERDCTL_VERSION=2.2.0
curl -sL https://github.com/containerd/nerdctl/releases/download/v\${NERDCTL_VERSION}/nerdctl-full-\${NERDCTL_VERSION}-linux-amd64.tar.gz \
  | tar Cxzv /usr/local
# lower log level
sed -i -e 's/debug/info/' /usr/local/lib/systemd/system/stargz-snapshotter.service

# disabled for now (stargz is just too slow and unreliable, causes CPU thrash)
#systemctl daemon-reload
#systemctl enable --now stargz-snapshotter
# end stargz ----------


# install docker
# GPG key
sudo mkdir -p /etc/apt/keyrings
if [ ! -f /etc/apt/keyrings/docker.gpg ]; then
  curl -fsSL https://download.docker.com/linux/ubuntu/gpg | sudo gpg --dearmor -o /etc/apt/keyrings/docker.gpg
fi

# add apt repo
echo \
  "deb [arch=$(dpkg --print-architecture) signed-by=/etc/apt/keyrings/docker.gpg] https://download.docker.com/linux/ubuntu \
  $(lsb_release -cs) stable" | sudo tee /etc/apt/sources.list.d/docker.list > /dev/null

# install
sudo apt-get update
sudo apt-get install -y docker-ce docker-ce-cli containerd.io docker-compose-plugin

# disabled (see above)
### stargz configs
## install containerd config
#mkdir -p /etc/containerd
#cat <<EOF > /etc/containerd/config.toml
#version = 2
#[proxy_plugins]
#  [proxy_plugins.stargz]
#    type = "snapshot"
#    address = "/run/containerd-stargz-grpc/containerd-stargz-grpc.sock"
#EOF
## install docker config
#mkdir -p /etc/docker
#cat <<EOF > /etc/docker/daemon.json
#{
#  "features": {
#    "containerd-snapshotter": true
#  },
#  "storage-driver": "stargz"
#}
#EOF
#systemctl restart docker docker.socket containerd

# ---
`;

  const opts: ConfigureOpts = {
    baseBootstrap,
    pullCmd: 'docker compose pull --quiet',
    upCmd: 'docker compose up --quiet-pull --detach',
    proxyServices: [
      '/lib/systemd/system/containerd.service',
      '/lib/systemd/system/docker.service',
      // '/usr/local/lib/systemd/system/stargz-snapshotter.service',
    ],
  };

  return mkConfigureScriptWithOpts(manifest, values, opts, fn);
}

export const dockerCompose = {
  mkConfigureScript,
  prepareConfigureData,
} as ConfigureBuilder;

export default dockerCompose;
